// src/components/admin/TableContract.tsx

import React, { useEffect, useState } from 'react';
import {
  fetchContracts,
  confirmContract,
  cancelContract,
  Contract as APIContract,
} from '@/API/admin/contract';
import {
  postAdminContractSubmit,
  getAdminAuthCheck,
  postApplicantResendIdentifyingImages,
  postAdminContractChange,
} from '@/API/apiService';

// API 側の Contract 型に不足しているプロパティのみを追加して拡張する
export interface Contract extends APIContract {
  public_contract_id: string;
  applicant_name: string;
  property_name: string;
  room_type_id: number;
  room_type_name: string;
  changed_histories?: {
    start_date?: { before: string; after: string; date: string };
    end_date?: { before: string; after: string; date: string };
    monthly_prices?: Array<{ before: number; after: number; date: string }>;
  };
  last_date_sent?: string;
  monthly_price: number;
  site_controller_book_id: string;
  start_date: string;
  end_date: string;
  contract_status_id: number;
  contract_status_name: string;
}

type PendingAction = 'confirm' | 'cancel' | 'mailSend' | 'applicantSend';

export const TableContract: React.FC = () => {
  const [contracts, setContracts] = useState<Contract[]>([]);
  const [isEditModalOpen, setEditModalOpen] = useState(false);
  const [isHistoryModalOpen, setHistoryModalOpen] = useState(false);
  const [selectedContract, setSelectedContract] = useState<Contract | null>(
    null
  );
  const [editStartDate, setEditStartDate] = useState('');
  const [editEndDate, setEditEndDate] = useState('');
  const [editMonthlyPrice, setEditMonthlyPrice] = useState('');
  const [feedbackModalMessage, setFeedbackModalMessage] = useState<
    string | null
  >(null);
  const [pendingAction, setPendingAction] = useState<{
    action: PendingAction;
    contract_id: number | string;
  } | null>(null);

  const loadContracts = async () => {
    const data = await fetchContracts();
    setContracts(data as Contract[]);
  };

  useEffect(() => {
    loadContracts();
  }, []);

  const handleConfirm = async (contract_id: number | string) => {
    try {
      await confirmContract(contract_id);
      await loadContracts();
      setFeedbackModalMessage('契約確定処理に成功しました。');
    } catch {
      setFeedbackModalMessage('契約確定処理に失敗しました。');
    }
  };

  const handleCancel = async (contract_id: number | string) => {
    try {
      await cancelContract(contract_id);
      await loadContracts();
      setFeedbackModalMessage('契約キャンセル処理に成功しました。');
    } catch {
      setFeedbackModalMessage('契約キャンセル処理に失敗しました。');
    }
  };

  // 「保証会社に送付」処理
  const handleMailSend = async (contract_id: number | string) => {
    try {
      // 認証チェックして userId を取得
      const authResponse = await getAdminAuthCheck();
      const userId = authResponse.data.user.id;

      const contract = contracts.find((c) => c.contract_id === contract_id);
      if (!contract) {
        setFeedbackModalMessage('対象の契約が見つかりません');
        return;
      }

      const formData = new FormData();
      // applicant_id は必須
      formData.append('applicant_id', String(contract.applicant_id));
      // 認証済みユーザー ID
      formData.append('user_id', String(userId));

      // URL に渡すのは契約 ID
      await postAdminContractSubmit(contract.contract_id, formData);

      setFeedbackModalMessage('保証会社への送付処理に成功しました。');
    } catch (error) {
      console.error('保証会社への送付処理に失敗しました。', error);
      setFeedbackModalMessage('保証会社への送付処理に失敗しました。');
    }
  };

  const handleApplicantSend = async (contract_id: number | string) => {
    try {
      const contract = contracts.find((c) => c.contract_id === contract_id);
      if (!contract) {
        setFeedbackModalMessage('対象の契約が見つかりません');
        return;
      }
      const authResponse = await getAdminAuthCheck();
      const userId = authResponse.data.user.id;
      const formData = new FormData();
      formData.append('applicant_id', String(contract.applicant_id));
      formData.append('user_id', String(userId));
      formData.append('mail_to', 'operator');
      await postApplicantResendIdentifyingImages(
        contract.contract_id,
        formData
      );
      Array.from(formData.entries()).forEach(([key, value]) => {
        console.log(`${key}: ${value}`);
        console.log(`AAA`);
      });
      setFeedbackModalMessage('運営への送付処理に成功しました。');
    } catch {
      setFeedbackModalMessage('運営への送付処理に失敗しました。');
    }
  };

  const handleOpenEditModal = (contract: Contract) => {
    setSelectedContract(contract);
    setEditStartDate(contract.start_date);
    setEditEndDate(contract.end_date);
    setEditMonthlyPrice(String(contract.monthly_price));
    setEditModalOpen(true);
  };

  const handleCloseEditModal = () => {
    setEditModalOpen(false);
    setSelectedContract(null);
  };

  const handleSaveEdit = async () => {
    if (!selectedContract) return;
    const authResponse = await getAdminAuthCheck();
    const userId = authResponse.data.user.id;

    const formData = new FormData();
    formData.append('id', String(selectedContract.contract_id));
    formData.append('user_id', String(userId));
    formData.append('start_date_before', selectedContract.start_date);
    formData.append('end_date_before', selectedContract.end_date);
    formData.append(
      'monthly_price_before',
      String(selectedContract.monthly_price)
    );
    formData.append('start_date_after', editStartDate);
    formData.append('end_date_after', editEndDate);
    formData.append('monthly_price_after', editMonthlyPrice);

    try {
      await postAdminContractChange(selectedContract.contract_id, formData);
      await loadContracts();
      handleCloseEditModal();
      setFeedbackModalMessage('契約変更処理に成功しました。');
    } catch {
      setFeedbackModalMessage('契約変更処理に失敗しました。');
    }
  };

  const handleOpenHistoryModal = (contract: Contract) => {
    setSelectedContract(contract);
    setHistoryModalOpen(true);
  };

  const handleCloseHistoryModal = () => {
    setHistoryModalOpen(false);
    setSelectedContract(null);
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'canceled':
        return 'キャンセル';
      case 'application_confirmed':
        return '申し込み完了';
      case 'booking_confirmed':
        return '予約完了';
      default:
        return status;
    }
  };

  const getConfirmationMessage = (action: PendingAction) => {
    switch (action) {
      case 'confirm':
        return '本当に契約を確定しますか？';
      case 'cancel':
        return '本当に契約をキャンセルしますか？';
      case 'mailSend':
        return '保証会社に送付してもよろしいですか？';
      case 'applicantSend':
        return '運営に送付してもよろしいですか？';
    }
  };

  const executePendingAction = async () => {
    if (!pendingAction) return;
    const { action, contract_id } = pendingAction;
    if (action === 'confirm') await handleConfirm(contract_id);
    else if (action === 'cancel') await handleCancel(contract_id);
    else if (action === 'mailSend') await handleMailSend(contract_id);
    else if (action === 'applicantSend') await handleApplicantSend(contract_id);
    setPendingAction(null);
  };

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full bg-white rounded">
        <thead className="bg-white border-b-2 border-[#DDDDDD]">
          <tr>
            <th className="p-3 text-left text-[10px]">名前</th>
            <th className="p-3 text-left text-[10px]">物件名</th>
            <th className="p-3 text-left text-[10px]">部屋タイプ</th>
            <th className="p-3 text-left text-[10px]">開始日</th>
            <th className="p-3 text-left text-[10px]">終了日</th>
            <th className="p-3 text-left text-[10px]">ステータス</th>
            <th className="p-3 text-left text-[10px]">キャンセル</th>
            <th className="p-3 text-left text-[10px]">変更</th>
            <th className="p-3 text-left text-[10px]">変更履歴</th>
            <th className="p-3 text-left text-[10px]">保証会社に送付</th>
            <th className="p-3 text-left text-[10px]">運営に送付</th>
          </tr>
        </thead>
        <tbody>
          {contracts.map((item) => (
            <tr
              key={String(item.contract_id)}
              className="border-b border-[#DDDDDD]"
            >
              <td className="px-3 py-4 text-[10px]">{item.applicant_name}</td>
              <td className="px-3 py-4 text-[10px]">{item.property_name}</td>
              <td className="px-3 py-4 text-[10px]">{item.room_type_name}</td>
              <td className="px-3 py-4 text-[10px]">{item.start_date}</td>
              <td className="px-3 py-4 text-[10px]">{item.end_date}</td>
              <td className="px-3 py-4 text-[10px]">
                <div className="flex flex-col">
                  <span className="bg-[#DAE9F2] px-2 py-1 rounded text-[10px] text-center">
                    {getStatusLabel(item.contract_status_name)}
                  </span>
                  {item.contract_status_name !== 'canceled' &&
                    item.contract_status_name !== 'booking_confirmed' && (
                      <span
                        className="underline text-[10px] cursor-pointer mt-1"
                        onClick={() =>
                          setPendingAction({
                            action: 'confirm',
                            contract_id: item.contract_id,
                          })
                        }
                      >
                        確定する
                      </span>
                    )}
                </div>
              </td>
              <td className="px-3 py-4 text-[10px]">
                {item.contract_status_name !== 'canceled' ? (
                  <span
                    className="underline text-[10px] cursor-pointer"
                    onClick={() =>
                      setPendingAction({
                        action: 'cancel',
                        contract_id: item.contract_id,
                      })
                    }
                  >
                    キャンセル
                  </span>
                ) : (
                  <span className="text-gray-400 text-[10px] cursor-not-allowed">
                    キャンセル
                  </span>
                )}
              </td>
              <td className="px-3 py-4 text-[10px]">
                <span
                  className="underline text-[10px] cursor-pointer"
                  onClick={() => handleOpenEditModal(item)}
                >
                  変更
                </span>
              </td>
              <td className="px-3 py-4 text-[10px]">
                <span
                  className="underline text-[10px] cursor-pointer"
                  onClick={() => handleOpenHistoryModal(item)}
                >
                  変更履歴
                </span>
              </td>
              <td className="px-3 py-4 text-[10px]">
                <div className="flex flex-col">
                  <span
                    className="underline text-[10px] cursor-pointer"
                    onClick={() =>
                      setPendingAction({
                        action: 'mailSend',
                        contract_id: item.contract_id,
                      })
                    }
                  >
                    保証会社に送付
                  </span>
                  <span className="text-gray-500 text-[10px]">
                    {item.last_date_sent
                      ? `最終送付：${item.last_date_sent}`
                      : '未送信'}
                  </span>
                </div>
              </td>
              <td className="px-3 py-4 text-[10px]">
                <span
                  className="underline text-[10px] cursor-pointer"
                  onClick={() =>
                    setPendingAction({
                      action: 'applicantSend',
                      contract_id: item.contract_id,
                    })
                  }
                >
                  運営に送付
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* 編集モーダル */}
      {isEditModalOpen && selectedContract && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white p-6 rounded w-96">
            <h2 className="text-lg font-bold mb-4 text-[10px]">契約変更</h2>
            <div className="mb-4">
              <label className="block mb-1 text-[10px]">契約開始日</label>
              <input
                type="date"
                value={editStartDate}
                onChange={(e) => setEditStartDate(e.target.value)}
                className="border p-2 w-full text-[10px]"
              />
            </div>
            <div className="mb-4">
              <label className="block mb-1 text-[10px]">契約終了日</label>
              <input
                type="date"
                value={editEndDate}
                onChange={(e) => setEditEndDate(e.target.value)}
                className="border p-2 w-full text-[10px]"
              />
            </div>
            <div className="mb-4">
              <label className="block mb-1 text-[10px]">家賃</label>
              <input
                type="number"
                value={editMonthlyPrice}
                onChange={(e) => setEditMonthlyPrice(e.target.value)}
                className="border p-2 w-full text-[10px]"
              />
            </div>
            <div className="flex justify-end">
              <button
                onClick={handleCloseEditModal}
                className="mr-4 bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded text-[10px]"
              >
                キャンセル
              </button>
              <button
                onClick={handleSaveEdit}
                className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded text-[10px]"
              >
                保存
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 履歴モーダル */}
      {isHistoryModalOpen && selectedContract && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white p-6 rounded w-96">
            <h2 className="text-lg font-bold mb-4 text-[10px]">変更履歴</h2>
            <div className="text-[10px]">
              {selectedContract.changed_histories?.start_date && (
                <p>
                  開始日: {selectedContract.changed_histories.start_date.before}{' '}
                  → {selectedContract.changed_histories.start_date.after}{' '}
                  （変更日: {selectedContract.changed_histories.start_date.date}
                  ）
                </p>
              )}
              {selectedContract.changed_histories?.end_date && (
                <p>
                  終了日: {selectedContract.changed_histories.end_date.before} →{' '}
                  {selectedContract.changed_histories.end_date.after} （変更日:{' '}
                  {selectedContract.changed_histories.end_date.date}）
                </p>
              )}
              {selectedContract.changed_histories?.monthly_prices?.length ? (
                <>
                  <p>家賃変更履歴:</p>
                  <ul className="list-disc list-inside">
                    {selectedContract.changed_histories.monthly_prices.map(
                      (e, i) => (
                        <li key={i}>
                          {e.before} → {e.after} （変更日: {e.date}）
                        </li>
                      )
                    )}
                  </ul>
                </>
              ) : (
                <p>変更履歴はありません。</p>
              )}
            </div>
            <div className="flex justify-end mt-4">
              <button
                onClick={handleCloseHistoryModal}
                className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded text-[10px]"
              >
                閉じる
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 確認モーダル */}
      {pendingAction && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white p-6 rounded w-80">
            <p className="mb-4 text-[10px]">
              {getConfirmationMessage(pendingAction.action)}
            </p>
            <div className="flex justify-end">
              <button
                onClick={() => setPendingAction(null)}
                className="mr-4 bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded text-[10px]"
              >
                キャンセル
              </button>
              <button
                onClick={executePendingAction}
                className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded text-[10px]"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      {/* フィードバックモーダル */}
      {feedbackModalMessage && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white p-6 rounded w-80">
            <p className="mb-4 text-[10px]">{feedbackModalMessage}</p>
            <button
              onClick={() => setFeedbackModalMessage(null)}
              className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded text-[10px] block mx-auto"
            >
              OK
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TableContract;
